import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteFooter from "@/components/SiteFooter";
import AnonymityGuarantee from "@/components/Landing/AnonymityGuarantee";
import TrustStats from "@/components/Landing/TrustStats";
import PricingTransparency from "@/components/Landing/PricingTransparency";
import {
  Activity, ShieldCheck, Zap, BarChart3, CheckCircle2, ArrowRight,
  Cog, Network, Layers, Award
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1767112601499-1b78cfb31a62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwaW5kdXN0cmlhbCUyMGFic3RyYWN0JTIwbWFjaGluZXJ5fGVufDB8fHx8MTc3ODAyNzAwNnww&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG = "https://images.unsplash.com/photo-1759159091682-3b98f4759367?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZyUyMGdlYXIlMjBwcmVjaXNpb258ZW58MHx8fHwxNzc4MDI3MDAyfDA&ixlib=rb-4.1.0&q=85";

const howItWorks = [
  ["01", "Create RFQ", "Buyer submits a structured sourcing request in under three minutes. Identity stays anonymous until award."],
  ["02", "Matched & quoted", "Engine routes the RFQ only to verified suppliers with the right category and stock. Quotes arrive ranked by value."],
  ["03", "Award & fulfill", "Buyer awards the winning quote, supplier identity is revealed, and the order is tracked to delivery."],
];

const principles = [
  { icon: ShieldCheck, t: "Verify first", b: "Every buyer and supplier is business-verified by our admin team before touching an RFQ." },
  { icon: Layers, t: "Structure beats free-text", b: "Standardised product fields and spec matching so quotes are actually comparable." },
  { icon: Award, t: "Rank by value", b: "Weighted scoring across price, lead time and supplier reliability — not just cheapest." },
  { icon: Network, t: "Designed for repeat", b: "Built for the operations teams who source week after week, not one-off transactions." },
];

const featurePillars = [
  { icon: ShieldCheck, t: "Verify-first network", b: "No noise, no consumer clutter, no anonymous shell companies. Only Canadian businesses with verified credentials reach your RFQ." },
  { icon: Zap, t: "Structured RFQs", b: "Required category, region, urgency, spec, and validity — every RFQ generates apples-to-apples quotes you can compare in seconds." },
  { icon: BarChart3, t: "Ranked by value", b: "50% price · 30% lead time · 20% supplier rating. Default scoring tuned for industrial MRO; not a marketplace race-to-the-bottom." },
];

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="lp-root">
      {/* ── Header ───────────────────────────────────────────── */}
      <header className={`lp-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="lp-container lp-header-inner">
          <Link to="/" className="lp-brand">
            <div className="lp-logo-mark"><Activity size={18} color="var(--navy-900)" strokeWidth={2.5} /></div>
            <div>
              <div className="font-serif-display lp-brand-name">CanadaMRO</div>
              <div className="font-mono lp-brand-sub">EXCHANGE</div>
            </div>
          </Link>
          <nav className="lp-nav">
            <Link to="/login" data-testid="landing-login-link" className="lp-link">Log in</Link>
            <Link to="/register" data-testid="landing-register-link" className="lp-cta-pill">
              Request access <ArrowRight size={14} />
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-bg" style={{ backgroundImage: `url(${HERO_IMG})` }} aria-hidden="true" />
        <div className="lp-hero-overlay" aria-hidden="true" />
        <div className="lp-grain" aria-hidden="true" />
        <div className="lp-container lp-hero-content">
          <div className="lp-eyebrow">
            <span className="lp-eyebrow-dot" /> ONTARIO · CANADA — INDUSTRIAL MRO NETWORK
          </div>
          <h1 className="font-serif-display lp-h1">
            Industrial sourcing<br/>
            <span className="lp-h1-accent">at the speed of the shop floor.</span>
          </h1>
          <p className="lp-lede">
            CanadaMRO Exchange is a controlled RFQ platform for industrial buyers and verified suppliers.
            Submit a structured request, receive ranked quotes from the right suppliers,
            award in clicks, and track every order to delivery.
          </p>
          <div className="lp-hero-cta">
            <Link to="/register?role=buyer" data-testid="cta-buyer" className="lp-btn-primary">
              Request access <ArrowRight size={16} />
            </Link>
            <a href="#how-it-works" data-testid="cta-how" className="lp-btn-secondary">
              How it works
            </a>
          </div>
          <div style={{ marginTop: 14, fontSize: 12, color: "var(--ink-mute)" }}>
            <Link to="/privacy" data-testid="cta-anonymity-link" style={{ color: "var(--amber-400)" }}>
              Read our anonymity policy →
            </Link>
          </div>

          {/* Hero meta strip */}
          <div className="lp-hero-meta font-mono">
            <div><span className="lp-amber">█</span> LIVE</div>
            <div>VERIFIED · 100%</div>
            <div>EN / FR · READY</div>
            <div>v1.0 · SOFT-LAUNCH</div>
          </div>
        </div>
      </section>

      {/* ── Trust strip (live stats) ─────────────────────────── */}
      <TrustStats />

      {/* ── Pricing transparency (admin-editable tagline) ────── */}
      <PricingTransparency />

      {/* ── Anonymity guarantee (highest-leverage trust pitch) ─ */}
      <AnonymityGuarantee />

      {/* ── How it works ─────────────────────────────────────── */}
      <section className="lp-section" id="how-it-works">
        <div className="lp-container">
          <div className="lp-section-head">
            <div className="font-mono lp-section-tag">/ 01 · HOW IT WORKS</div>
            <h2 className="font-serif-display lp-h2">Three steps,<br/>end-to-end.</h2>
          </div>
          <div className="lp-how-grid">
            {howItWorks.map(([n, t, b], i) => (
              <article key={n} className="lp-how-card" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="font-mono lp-how-num">{n}</div>
                <h3 className="font-serif-display lp-how-title">{t}</h3>
                <p className="lp-how-body">{b}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Principles · 2x2 with feature image ──────────────── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-container">
          <div className="lp-principles-grid">
            <div className="lp-principles-text">
              <div className="font-mono lp-section-tag">/ 02 · PRODUCT PRINCIPLES</div>
              <h2 className="font-serif-display lp-h2">
                Built for operations teams,<br/>not marketers.
              </h2>
              <p className="lp-lede" style={{ maxWidth: 540 }}>
                In industrial procurement, speed and reliability matter more than polished branding.
                We built a controlled environment where the right supplier gets the right RFQ,
                and the buyer gets usable comparisons.
              </p>
              <div className="lp-principles-list">
                {principles.map(({ icon: Icon, t, b }, i) => (
                  <div key={t} className="lp-principle-item" style={{ animationDelay: `${i * 80}ms` }}>
                    <div className="lp-principle-icon"><Icon size={18} color="var(--amber-500)" /></div>
                    <div>
                      <div className="lp-principle-title">{t}</div>
                      <div className="lp-principle-body">{b}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lp-principles-media" aria-hidden="true">
              <img src={FEATURE_IMG} alt="" loading="lazy" />
              <div className="lp-media-frame" />
              <div className="lp-media-tag font-mono">PRECISION · MATCHED</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature pillars ──────────────────────────────────── */}
      <section className="lp-section">
        <div className="lp-container">
          <div className="lp-section-head">
            <div className="font-mono lp-section-tag">/ 03 · WHAT MAKES IT WORK</div>
            <h2 className="font-serif-display lp-h2">Three pillars. Zero noise.</h2>
          </div>
          <div className="lp-pillars-grid">
            {featurePillars.map(({ icon: Icon, t, b }, i) => (
              <article key={t} className="lp-pillar-card" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="lp-pillar-icon"><Icon size={22} color="var(--amber-500)" /></div>
                <h3 className="font-serif-display lp-pillar-title">{t}</h3>
                <p className="lp-pillar-body">{b}</p>
                <div className="lp-pillar-rule" />
                <div className="font-mono lp-pillar-meta">
                  <CheckCircle2 size={12} color="var(--amber-500)" /> ENABLED BY DEFAULT
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="lp-cta-section">
        <div className="lp-container lp-cta-inner">
          <Cog size={56} color="var(--amber-500)" strokeWidth={1.4} className="lp-cta-cog" />
          <h2 className="font-serif-display lp-h2" style={{ textAlign: "center" }}>
            Put your sourcing on rails.
          </h2>
          <p className="lp-lede" style={{ textAlign: "center", maxWidth: 620, margin: "16px auto 32px" }}>
            Join the verified Canadian MRO network. Start in Ontario. Ship faster.
          </p>
          <div className="lp-hero-cta" style={{ justifyContent: "center" }}>
            <Link to="/register" data-testid="cta-register" className="lp-btn-primary">
              Create free account <ArrowRight size={16} />
            </Link>
            <Link to="/login" data-testid="cta-login" className="lp-btn-secondary">
              Log in
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer (admin-managed via /api/public/site-settings) ─ */}
      <SiteFooter />
    </div>
  );
}
